import { useEffect } from 'react';
import useApiEndpoints from '../../context/useApiEndpoints';
import useApi from '../useApi';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useToast } from 'src/app/useToast';
import { BunkeringEventBM } from 'src/types/BusinessModel/Bunkering/BunkeringEventBM';
import { mapBunkeringBMs, mapBunkeringEventBMs, mapFuelRobBMs, mapBunkerLossBMs } from 'src/factory/Bunkering/BunkeringFactory';
import { useBunkerContext } from 'src/context/BunkerContext';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { UUID } from 'crypto';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { useBunkerUpdateHandler } from 'src/app/useBunkerUpdateHandler';
import { useAuthStore } from 'src/context/AuthContext';
import { BunkerLossBM } from 'src/types/BusinessModel/Bunkering/BunkerLossBM';
import { BunkerReceivedBM } from 'src/types/BusinessModel/Bunkering/BunkerReceivedBM';
import { getAllBunkeringItems, getAllBunkeringStatusItems, getAllFuelRobItems } from './offlineBunkeringApi';
import { isFuelRobResponseStillValid, isBunkerLossResponseStillValid, markAuthoritativeFuelRobApplied, markAuthoritativeBunkerLossApplied } from './bunkeringDataGuard';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData, postApiData, deleteApiData } = useApi();
    const { setBunkeringEvents, bunkeringEvents, setBunkerings, setFuelRobData, setBunkerLossData, setDeletedBunkerings } = useBunkerContext();
    const { processApiEndpointError } = apiEndpointUtils();
    const { toastError } = useToast();
    const { handleUpdatedBunkering, handleUpdatedBunkeringStatus, handleDeletedBunkeringStatus, handleUpdatedRobs, handleUpdatedBunkerLoss, handleUpdatedDebunkering } =
        useBunkerUpdateHandler();
    const { signalRConnectionId } = useAuthStore();

    const getBunkeringEvents = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        callInBackground: boolean,
        skipUpdatingContext: boolean = false
    ) => {
        const response = getApiData({
            endpoint: apiEndpoints['getBunkeringEvents'].url,
            queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
            callInBackground: callInBackground,
        });

        if (skipUpdatingContext) {
            const bms = (await response) as BunkeringEventBM[];
            if (!Array.isArray(bms)) return;
            const _fuelRobData = mapBunkeringEventBMs(bms);
            return _fuelRobData;
        }
    };

    const getBunkerings = (imoNumber: number, fromDate: string, toDate: string, callInBackground: boolean) =>
        getApiData({
            endpoint: apiEndpoints['getBunkerings'].url,
            queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
            callInBackground: callInBackground,
        });

    const upsertBunkering = async (imoNumber: number, bunkering: BunkeringBM, saveId?: string) => {
        const result = await postApiData({
            endpoint: apiEndpoints['upsertBunkering'].url,
            data: bunkering,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, ...(saveId ? { _saveId: saveId } : {}) }),
            callInBackground: true,
        });

        if (saveId) {
            window.dispatchEvent(new CustomEvent('save-api-fired', { detail: { saveId } }));
        }

        return result;
    };

    const getFuelRobData = async (
        imoNumber: number,
        noonReportIds: number[],
        bunkeringGuids: UUID[],
        callInBackground: boolean = false,
        skipUpdatingContext: boolean = false,
        forceRefreshCache: boolean = false
    ) => {
        const noonReportIdsStr = noonReportIds.join(',');
        const bunkeringGuidsStr = bunkeringGuids.join(',');
        // issuedAt is captured at request time (not response time) so a response can be checked
        // against any more-authoritative update (e.g. a post-offline-sync refresh) that was
        // applied *after* this request was issued but *before* it resolved - see
        // bunkeringDataGuard.ts for why this matters.
        const issuedAt = Date.now();
        const response = getApiData({
            endpoint: apiEndpoints['getFuelRobData'].url,
            queryParam: buildQueryParams({
                imoNumber,
                noonReportIds: noonReportIdsStr,
                bunkeringGuids: bunkeringGuidsStr,
                connectionId: signalRConnectionId,
            }),
            additionalInfo: { skipUpdatingContext: skipUpdatingContext, issuedAt: issuedAt },
            callInBackground: callInBackground,
            forceRefreshCache: forceRefreshCache,
        });

        if (skipUpdatingContext) {
            const bms = (await response) as FuelRobBM[];
            if (!Array.isArray(bms)) return;
            const offlineFuelRobs = await getAllFuelRobItems();
            const _fuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
            return _fuelRobData;
        }
    };

    const upsertFuelRobData = (fuelRobData: FuelRobBM[], imoNumber: number, saveId?: string) =>
        postApiData({
            endpoint: apiEndpoints['upsertFuelRobData'].url,
            data: fuelRobData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, ...(saveId ? { _saveId: saveId } : {}) }),
            callInBackground: true,
        });

    const upsertBunkeringStatus = async (status: BunkeringStatusBM, imoNumber: number, saveId?: string) => {
        const result = await postApiData({
            endpoint: apiEndpoints['upsertBunkeringStatus'].url,
            data: status,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, ...(saveId ? { _saveId: saveId } : {}) }),
            callInBackground: true,
        });

        if (saveId) {
            window.dispatchEvent(new CustomEvent('save-api-fired', { detail: { saveId } }));
        }

        return result;
    };

    const deleteBunkeringStatus = (bunkeringStatusId: number, bunkeringStatusGuid: UUID, imoNumber: number) =>
        deleteApiData({
            endpoint: apiEndpoints['deleteBunkeringStatus'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, bunkeringStatusId, bunkeringStatusGuid }),
            additionalInfo: { bunkeringStatusId, bunkeringStatusGuid },
        });

    const getBunkerLossData = (noonReportIds: number[], imoNumber: number, forceRefreshCache: boolean = false) =>
        getApiData({
            endpoint: apiEndpoints['getBunkerLossData'].url,
            queryParam: buildQueryParams({ imoNumber, noonReportIds }),
            additionalInfo: { issuedAt: Date.now() },
            forceRefreshCache: forceRefreshCache,
        });

    const upsertBunkerLossData = async (bunkerLossData: BunkerLossBM[], imoNumber: number, noonReportId: number, saveId?: string) => {
        const result = await postApiData({
            endpoint: apiEndpoints['upsertBunkerLossData'].url,
            data: bunkerLossData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, noonReportId, ...(saveId ? { _saveId: saveId } : {}) }),
            callInBackground: true,
            additionalInfo: { noonReportId },
        });

        if (saveId) {
            window.dispatchEvent(new CustomEvent('save-api-fired', { detail: { saveId } }));
        }

        return result;
    };

    const upsertDebunkeringData = (debunkeringData: BunkerReceivedBM[], imoNumber: number) =>
        postApiData({
            endpoint: apiEndpoints['upsertDebunkeringData'].url,
            data: debunkeringData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            callInBackground: true,
        });

    useEffect(() => {
        (async () => {
            if (state.status === 'success') {
                if (state.endpoint === apiEndpoints['getBunkeringEvents'].url) {
                    const bms = state.result as BunkeringEventBM[];
                    const _bunkeringEvents = mapBunkeringEventBMs(bms);
                    setBunkeringEvents(_bunkeringEvents);
                } else if (state.endpoint === apiEndpoints['getBunkerings'].url) {
                    const bms = state.result as BunkeringBM[];
                    const offlineBunkerings = await getAllBunkeringItems();
                    const offlineBunkeringStatuses = await getAllBunkeringStatusItems();
                    const _bunkerings = mapBunkeringBMs(bms, bunkeringEvents, offlineBunkerings, offlineBunkeringStatuses);
                    setBunkerings(_bunkerings.bunkerings);
                    setDeletedBunkerings(_bunkerings.deletedBunkerings);
                } else if (state.endpoint === apiEndpoints['upsertBunkering'].url) {
                    const bm = state.result as BunkeringBM;
                    handleUpdatedBunkering(bm);
                } else if (state.endpoint === apiEndpoints['getFuelRobData'].url) {
                    if (state.additionalInfo?.skipUpdatingContext) return;
                    if (!isFuelRobResponseStillValid(state.additionalInfo?.issuedAt)) return;
                    const bms = state.result as FuelRobBM[];
                    const offlineFuelRobs = await getAllFuelRobItems();

                    const _fuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
                    setFuelRobData(_fuelRobData);
                    // Any successfully applied GET response becomes the new authoritative floor,
                    // so an older, slower-to-resolve response (e.g. one issued moments earlier by a
                    // different reconciliation effect) can never overwrite it afterward.
                    markAuthoritativeFuelRobApplied();
                } else if (state.endpoint === apiEndpoints['upsertFuelRobData'].url) {
                    const bms = state.result as FuelRobBM[];
                    const offlineFuelRobs = await getAllFuelRobItems();

                    handleUpdatedRobs(bms, offlineFuelRobs);
                    markAuthoritativeFuelRobApplied();
                    // Toast is handled in BunkerRobContainer save effect to avoid duplicate
                } else if (state.endpoint === apiEndpoints['upsertBunkeringStatus'].url) {
                    const bm = state.result as BunkeringStatusBM;
                    handleUpdatedBunkeringStatus(bm);
                } else if (state.endpoint === apiEndpoints['deleteBunkeringStatus'].url) {
                    const statusGuid = state.additionalInfo.bunkeringStatusGuid;
                    handleDeletedBunkeringStatus(statusGuid);
                } else if (state.endpoint === apiEndpoints['getBunkerLossData'].url) {
                    if (!isBunkerLossResponseStillValid(state.additionalInfo?.issuedAt)) return;
                    const bms = state.result as BunkerLossBM[];
                    const _bunkerLossData = mapBunkerLossBMs(bms);
                    setBunkerLossData(_bunkerLossData);
                    // See markAuthoritativeFuelRobApplied() above for why this is needed here too.
                    markAuthoritativeBunkerLossApplied();
                } else if (state.endpoint === apiEndpoints['upsertBunkerLossData'].url) {
                    const bms = state.result as BunkerLossBM[];
                    handleUpdatedBunkerLoss(bms, state.additionalInfo?.noonReportId);
                    markAuthoritativeBunkerLossApplied();
                } else if (state.endpoint === apiEndpoints['upsertDebunkeringData'].url) {
                    if (Array.isArray(state.result)) {
                        handleUpdatedDebunkering(state.result as BunkerReceivedBM[]);
                    } else {
                        const bm = state.result as BunkeringBM;
                        handleUpdatedBunkering(bm);
                    }
                }
            } else {
                if (state.endpoint === apiEndpoints['upsertBunkering'].url && state.error?.response?.status === 409) {
                    toastError(
                        'Cannot delete this BDN: consumption records exist for this bunkering. Remove consumption entries before deleting.',
                        8000
                    );
                } else {
                    processApiEndpointError(state);
                }
            }
        })();
    }, [state]);

    return {
        state,
        getBunkeringEvents,
        getBunkerings,
        upsertBunkering,
        getFuelRobData,
        upsertFuelRobData,
        upsertBunkeringStatus,
        deleteBunkeringStatus,
        getBunkerLossData,
        upsertBunkerLossData,
        upsertDebunkeringData,
    };
};
